import { Form } from '@ant-design/compatible';
import { Badge, Button, Tabs } from 'antd';
import SidePanel from 'common/side-panel/side-panel';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { FormattedMessage, injectIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import { Icon, notification } from 'ui';
import useDeepCompareEffect from 'use-deep-compare-effect';
import moment from 'utils/cops-moment';
import { withRouter } from 'utils/withRouter';
import { customDurationFormat } from '../../../../../utils/helpers';
import useDoNotShow from '../../../../../utils/hooks/useDoNotShow';
import ErrorHandler from '../../../../error/components/error-handler';
import ShortSteps from '../../../../publish-steps/containers/short-steps-container';
import { blockConfig, viewConfig } from '../../../config';
import { resetSocketState } from '../../../containers/gantt-actions';
import AbsenceEditSidepanelContent from './absence-edit-sidepanel-content';
import { getWarningDescription } from './block-detail-actions';
import BlockEditSidepanelContent from './block-edit-sidepanel-content';
import RescheduleItem from './reschedule-item';

const { TabPane } = Tabs;

const StyledTabs = styled(Tabs)`
  .ant-tabs-nav {
    font-size: 16px;
    font-weight: bold;
    text-transform: uppercase;
  }
  .ant-tabs-content {
    overflow-y: auto;
    height: calc(100vh - 120px - ${p => p.offsetHeight}px) !important;
  }
  .ant-tabs-bar {
    margin: 0 !important;
  }
  .ant-tabs-tab {
    font-weight: 400;
    font-size: 16px;
    text-transform: uppercase;
    width: 110px;
    justify-content: center;
  }

  .ant-tabs-ink-bar {
    background: ${p => p.theme.colors.theme} !important;
  }

  .ant-tabs-tab-active {
    .ant-tabs-tab-btn {
      color: ${p => p.theme.colors.theme} !important;
    }
  }
`;

const ConfirmButton = styled(Button)`
  color: white;
  background-color: #6f7e95;
  &:hover {
    opacity: 0.7;
    color: white;
    background-color: #6f7e95;
  }
`;

const NoData = styled.p`
  font-size: 16px;
`;

const TabTitle = styled.p`
  font-size: 16px;
  margin-right: 10px;
`;

const WarningRow = styled.div`
  width: calc(100% + 26px);
  margin-left: -13px;
  margin-top: ${p => p.hasOffset ? 0 : -13}px;
  border: 3px solid ${p => p.theme.colors.theme};
  background: rgba(0, 152, 204, 0.2);
  padding: 0 12px;
  margin-bottom: 10px;
  height: 30px;
  display: flex;
  align-items: center;
  font-weight: 600;
  i  {
    margin-right: 10px;
  }
`;

export const getPlannerInfo = block => block.lock ?
  `${block.lock.lockedByName} ${block.lock.lockedOn ?
    `(${moment(block.lock.lockedOn).fromNow()})` :
    ''}` :
  `${block?.assignedPlannerName} ${block.draftSince ?
    `(${moment(block.draftSince).fromNow()})` :
    ''}`;

const BlockEditSidepanel = ({
  match,
  history,
  intl,
  block,
  form,
  isLoadingBlockData = false,
  fetchBlockData,
  isDoingAction,
  markDateConfirmed,
  publishToSAP,
  updateBlockData,
  addBlockData,
  isNew = false,
  view = {},
  lanesInfo,
  addSuccess,
  fetchBlockDataSuccess,
  updateBlockId,
  splitBlock,
  deletePlanning,
  error,
  resetBlockData,
  markDateUnconfirmed,
  restoreBackup,
  recalculateTravelTime,
  openPlanningAndView,
  blockCurrentStatusses,
}) => {
  const dispatch = useDispatch();
  const { setFieldsValue } = form;
  const [doNotShowReschedule, checkbox] = useDoNotShow('confirmedToTentative');
  const {
    deletedPlanningFromSockets = [],
    updatedPlanningFromSockets = [],
  } = useSelector(state => state.gantt);
  const readOnly = block?.lock ||
    (!block?.isAssignedPlanner && !block?.isUserAdminForScope) ||
    !block.canMove ||
    block.outOfScope;
  const [stepsHeight, setStepsHeight] = useState(0);

  const close = () => {
    history.push(
      `${history.location.pathname.replace(/\/edit|new\/[\s\S]*/, '')}`,
    );
  };

  useEffect(() => () => {
    resetBlockData();
  }, []);

  useDeepCompareEffect(() => {
    if (
      block?.id &&
      deletedPlanningFromSockets?.find(d => d === block?.id)
    ) {
      close();
      notification.warning({
        message: intl?.formatMessage({ id: 'planningBoard.PLANNING_DELETED_SOCKETS_TITLE' }),
        placement: 'top',
        description: intl?.formatMessage({
          id: 'planningBoard.PLANNING_DELETED_SOCKETS_DESCRIPTION',
        }),
        key: 'planningDeletedSockets',
        className: 'extended-notification',
      });
      dispatch(resetSocketState([block?.id]));
    }
  }, [deletedPlanningFromSockets]);

  useDeepCompareEffect(() => {
    if (
      block?.id &&
      updatedPlanningFromSockets?.find(d => d === block?.id)
    ) {
      notification.warning({
        message: intl?.formatMessage({ id: 'planningBoard.PLANNING_UPDATED_SOCKETS_TITLE' }),
        placement: 'top',
        description: intl?.formatMessage({
          id: 'planningBoard.PLANNING_UPDATED_SOCKETS_DESCRIPTION',
        }),
        key: 'planningUpdatedSockets',
        className: 'extended-notification',
      });
      dispatch(resetSocketState([block?.id]));
    }
  }, [updatedPlanningFromSockets]);

  useEffect(() => {
    if (!isNew && match.params.planningId) {
      fetchBlockData({
        id: match.params.planningId,
        boardId: viewConfig.boardIdTypes.service,
        isSidePanel: true,
      });
    } else {
      resetBlockData();
    }
  }, [match.params.planningId]);

  useEffect(() => {
    if (block?.isDeleted) {
      close();
    }
  }, [block?.isDeleted]);

  useEffect(() => {
    if (addSuccess) {
      close();
    }
  }, [addSuccess]);

  const onEngineerChange = (engineer) => {
    setFieldsValue({
      technicianNumber: engineer?.id,
      laneId: engineer?.id,
    });
  };

  const updateBlock = (callback) => {
    const isAbsenceOrReservation =
      match.params.type !== blockConfig.types.fieldService;
    const otherLane = block?.laneId !== form.getFieldValue('technicianNumber');
    const startDate = form.getFieldValue('startDate').format('YYYY_MM_DD');

    updateBlockData({
      oldLaneId: otherLane ? block.laneId : null,
      type: !otherLane ?
        'updateSameLane' :
        'updateOtherLane',
      boardId: viewConfig.boardIdTypes.service,
      isSidePanel: true,
      planningId: block.id,
      isAbsenceOrReservation,
      linkedEntityId: block.objectId,
      laneId: form.getFieldValue('laneId'),
      successCb: block.showAs === 'active' &&
        block.type === blockConfig.types.fieldService ?
        () => {
          if (
            !isAbsenceOrReservation &&
            !doNotShowReschedule &&
            startDate !== moment(block.start).format('YYYY_MM_DD')
          ) {
            notification.info({
              key: 'confirmedToTentative',
              placement: 'top',
              btn: <ConfirmButton
                type='secondary'
                onClick={() => notification.destroy('confirmedToTentative')}
              >
                {intl.formatMessage({ id: 'common.OK' })}
              </ConfirmButton>,
              message:
                intl.formatMessage({ id: 'planningBoard.blocks.PLANNED_TO_TENTATIVE_TITLE' }),
              description: <>
                {intl.formatMessage({
                  id: 'planningBoard.blocks.PLANNED_TO_TENTATIVE',
                })}
                {checkbox}
              </>,
              duration: 0,
              className: 'extended-notification',
            });
          }
          if (callback) {
            callback();
          }
        } :
        callback,
      skipReload: !!callback,
      description: isAbsenceOrReservation ? form.getFieldValue('descriptionAbsence') : null,
      start: moment(
        `${startDate} ${form.getFieldValue('startTime').format('HH:mm:ss')}`,
        'YYYY-MM-DD HH:mm:ss',
      ).set({ second: 0 }).format('YYYY-MM-DDTHH:mm:ss'),
      durations: !isAbsenceOrReservation ? [
        {
          type: blockConfig.activityTypes.travelTo,
          duration: customDurationFormat(form.getFieldValue('travelTo'), 'hours'),
        },
        {
          type: blockConfig.activityTypes.labour,
          duration: customDurationFormat(form.getFieldValue('durationLabour'), 'hours'),
        },
        {
          type: blockConfig.activityTypes.travelFrom,
          duration: customDurationFormat(form.getFieldValue('travelFrom'), 'hours'),
        },
      ] :
        [
          {
            type: match.params.type === blockConfig.types.absence ?
              blockConfig.activityTypes.nonWork :
              blockConfig.activityTypes.work,
            duration: customDurationFormat(
              form.getFieldValue('durationAbsence'),
              form.getFieldValue('durationDisplayUnit'),
            ),
            durationDisplayUnit: form.getFieldValue('durationDisplayUnit'),
          },
        ],
    });
  };

  const handleAction = (action, isUpdate) => {
    if (form.isFieldsTouched() && !isUpdate) {
      updateBlock(action);
    } else {
      action();
    }
  };

  const handleBlockAction = (action, isUpdate) => {
    if (block.isAssignedPlanner) {
      handleAction(action, isUpdate);
    } else if (!block.isAssignedPlanner && block.isUserAdminForScope) {
      notification.warning({
        message: intl.formatMessage({ id: 'planningBoard.blocks.ADMIN_WARNING_TITLE' }),
        description: getWarningDescription(() => {
          notification.destroy('assignedPlannerWarning');
          handleAction(action, isUpdate);
        }, intl),
        className: 'extended-notification',
        key: 'assignedPlannerWarning',
        duration: 0,
        placement: 'top',
      });
    }
  };

  const createBlock = () => {
    const isAbsenceOrReservation =
      match.params.type === blockConfig.types.absence ||
      match.params.type === blockConfig.types.reservation;

    addBlockData({
      boardId: viewConfig.boardIdTypes.service,
      laneId: match.params.laneId,
      isSidePanel: true,
      start: moment(
        // eslint-disable-next-line
        `${form.getFieldValue('startDate').format('YYYY_MM_DD')} ${form.getFieldValue('startTime').format('HH:mm:ss')}`,
        'YYYY-MM-DD HH:mm:ss',
      ).set({ second: 0 }).format('YYYY-MM-DDTHH:mm:ss'),
      type: match.params.type,
      description: form.getFieldValue('descriptionAbsence'),
      durationDisplayUnit: form.getFieldValue('durationDisplayUnit'),
      duration: !isAbsenceOrReservation ?
        customDurationFormat(form.getFieldValue('durationAbsence'), 'hours') :
        customDurationFormat(
          form.getFieldValue('durationAbsence'),
          form.getFieldValue('durationDisplayUnit'),
        ),
    });
  };

  const deleteBlock = () => {
    if (block.isCopsOnly) {
      close();
    }
    deletePlanning({
      boardId: viewConfig.boardIdTypes.service,
      planningId: block.id,
      isSidePanel: true,
    });
  };

  const getTitle = () => {
    if (!block?.isAssignedPlanner || !block?.canMove) {
      if (match.params.type === blockConfig.types.reservation) {
        return intl.formatMessage({ id: 'planningBoard.blocks.VIEW_RESERVATION' });
      } else if (match.params.type === blockConfig.types.absence) {
        return intl.formatMessage({ id: 'planningBoard.blocks.VIEW_ABSENCE' });
      }
      return intl.formatMessage({ id: 'planningBoard.blocks.VIEW_PLANNING' });
    }
    if (match.params.type === blockConfig.types.reservation) {
      return intl.formatMessage({ id: 'planningBoard.blocks.EDIT_RESERVATION' });
    } else if (match.params.type === blockConfig.types.absence) {
      return intl.formatMessage({ id: 'planningBoard.blocks.EDIT_ABSENCE' });
    }
    return intl.formatMessage({ id: 'planningBoard.blocks.EDIT_PLANNING' });
  };

  const isAbsenceOrReservation =
    match.params.type === blockConfig.types.absence ||
    match.params.type === blockConfig.types.reservation;

  return (
    <SidePanel
      width={'40%'}
      title={getTitle()}
      onPanelClose={close}
      scrollOnOverflow={false}
    >
      <ErrorHandler type='notification' error={error} />
      <ShortSteps
        id={block?.id}
        currentBlockStatus={
          blockCurrentStatusses?.find(b => b.id === block?.id)?.operationInfo ||
          block?.operationInfo ||
          {}
        }
        needsPolling={false}
        reloadData={
          blockId =>
            fetchBlockData({
              id: blockId,
              boardId: viewConfig.boardIdTypes.service,
              isSidePanel: true,
            })
        }
        onDataReloaded={fetchBlockDataSuccess}
        type='planning'
        updateId={(from, to) => updateBlockId({ from, to, laneId: block.laneId })}
        isSidePanel={true}
        isPlanningBoard={true}
        setStepsHeight={setStepsHeight}
        isGtsBlocked={block?.isGtsBlocked}
        gtsErrors={block?.gtsBlockErrors}
      />
      {block?.outOfScope ?
        <WarningRow hasOffset={stepsHeight > 0}>
          <Icon type='circle-info' />
          <FormattedMessage id='planning.OUT_OF_SCOPE' />
        </WarningRow> :
        null
      }
      {isAbsenceOrReservation ?
        // eslint-disable-next-line
        <AbsenceEditSidepanelContent
          createBlock={createBlock}
          updateBlock={() => handleBlockAction(updateBlock, true)}
          isLoadingBlockData={isLoadingBlockData}
          block={block}
          isDoingAction={isDoingAction}
          isNew={isNew}
          view={view}
          lanesInfo={lanesInfo}
          form={form}
          onEngineerChange={onEngineerChange}
          readOnly={block && Object.keys(block).length ? readOnly : false}
          close={close}
          deletePlanning={() => handleBlockAction(deleteBlock, true)}
          publishToSAP={v => handleBlockAction(() => publishToSAP({
            ...v,
            isSidePanel: true,
          }))}
        /> :
        <StyledTabs offsetHeight={stepsHeight || 20}>
          <TabPane key='1' tab={intl.formatMessage({ id: 'planningBoard.blocks.PLANNING' })}>
            <BlockEditSidepanelContent
              block={block}
              form={form}
              isLoadingBlockData={isLoadingBlockData}
              isDoingAction={isDoingAction}
              updateBlock={() => handleBlockAction(updateBlock, true)}
              isNew={isNew}
              view={view}
              onEngineerChange={onEngineerChange}
              openPlanningAndView={openPlanningAndView}
              markDateConfirmed={v => handleBlockAction(() => markDateConfirmed({
                ...v,
                isSidePanel: true,
              }))}
              publishToSAP={v => handleBlockAction(() => publishToSAP({
                ...v,
                isSidePanel: true,
              }))}
              markDateUnconfirmed={v => handleBlockAction(() => markDateUnconfirmed({
                ...v,
                isSidePanel: true,
              }))}
              restoreBackup={v => handleBlockAction(() => restoreBackup({
                ...v,
                isSidePanel: true,
              }))}
              recalculateTravelTime={v => handleBlockAction(() => recalculateTravelTime({
                ...v,
                isSidePanel: true,
              }))}
              readOnly={readOnly}
              close={close}
              deletePlanning={() => handleBlockAction(deleteBlock, true)}
              splitBlock={v => handleBlockAction(() => splitBlock(v))}
            />
          </TabPane>
          {!isAbsenceOrReservation ?
            <TabPane
              key='2'
              tab={
                <Badge count={block?.reschedules?.count}>
                  <TabTitle>
                    {intl.formatMessage({ id: 'planningBoard.blocks.RESCHEDULING' })}
                  </TabTitle>
                </Badge>
              }
            >
              {block.reschedules?.items?.length ?
                block.reschedules?.items?.map((r, i) => <RescheduleItem key={i} {...r} />) :
                <NoData>{intl.formatMessage({ id: 'common.placeholders.NO_DATA_FOUND' })}</NoData>
              }
            </TabPane> :
            null
          }
        </StyledTabs>
      }
    </SidePanel>
  );
};

BlockEditSidepanel.propTypes = {
  match: PropTypes.object,
  history: PropTypes.object,
  intl: PropTypes.object,
  block: PropTypes.object,
  form: PropTypes.object,
  isLoadingBlockData: PropTypes.bool,
  fetchBlockData: PropTypes.func,
  isDoingAction: PropTypes.bool,
  markDateConfirmed: PropTypes.func,
  publishToSAP: PropTypes.func,
  updateBlockData: PropTypes.func,
  isNew: PropTypes.bool,
  addBlockData: PropTypes.func,
  view: PropTypes.object,
  lanesInfo: PropTypes.array,
  addSuccess: PropTypes.bool,
  fetchBlockDataSuccess: PropTypes.func,
  updateBlockId: PropTypes.func,
  splitBlock: PropTypes.func,
  deletePlanning: PropTypes.func,
  error: PropTypes.object,
  resetBlockData: PropTypes.func,
  markDateUnconfirmed: PropTypes.func,
  restoreBackup: PropTypes.func,
  recalculateTravelTime: PropTypes.func,
  openPlanningAndView: PropTypes.func,
  blockCurrentStatusses: PropTypes.array,
};

export default Form.create()(withRouter(injectIntl(BlockEditSidepanel)));
